/**
 * TimelineStrip — horizontal bar visualization of movement segments.
 *
 * Renders each segment proportionally within the [from, to] window.
 * Visual encoding:
 *   - Background colour: MOVING (green), STOPPED (amber), UNKNOWN (gray)
 *   - Border style: OBSERVED (solid), DECLARED (dashed), INFERRED (dotted)
 *   - Hover tooltip with duration + evidence count
 *   - Click to select a segment
 */

import { useMemo } from 'react';
import type { MovementSegment } from '../../../types/vehicleMovementTimeline';

interface TimelineStripProps {
  segments: MovementSegment[];
  from: string;
  to: string;
  selectedSegmentId: string | null;
  onSelectSegment: (segment: MovementSegment | null) => void;
}

// ── Colour / style maps ────────────────────────────────────────────────────

const TYPE_COLOURS: Record<string, string> = {
  MOVING: 'bg-success',
  STOPPED: 'bg-warning',
  UNKNOWN: 'bg-secondary',
};

const TYPE_COLOURS_SELECTED: Record<string, string> = {
  MOVING: 'bg-success',
  STOPPED: 'bg-warning',
  UNKNOWN: 'bg-accent',
};

const SOURCE_BORDER: Record<string, string> = {
  OBSERVED_TELEMETRY: 'border-solid',
  DECLARED_LEG: 'border-dashed',
  INFERRED_LEG: 'border-dotted',
};

const SOURCE_LABEL: Record<string, string> = {
  OBSERVED_TELEMETRY: 'Observed',
  DECLARED_LEG: 'Declared',
  INFERRED_LEG: 'Inferred',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.round((ms % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Component ───────────────────────────────────────────────────────────────

export default function TimelineStrip({
  segments,
  from,
  to,
  selectedSegmentId,
  onSelectSegment,
}: TimelineStripProps) {
  const windowMs = useMemo(
    () => Date.parse(to) - Date.parse(from),
    [from, to],
  );
  const fromMs = useMemo(() => Date.parse(from), [from]);

  // Time axis labels
  const axisLabels = useMemo(() => {
    const labels: { pct: number; label: string }[] = [];
    const step = windowMs / 4;
    for (let i = 0; i <= 4; i++) {
      const t = new Date(fromMs + step * i);
      labels.push({
        pct: (i / 4) * 100,
        label: t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      });
    }
    return labels;
  }, [fromMs, windowMs]);

  if (segments.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 text-center text-muted-foreground text-sm">
        No segments returned. Try adjusting the time window.
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4 space-y-2">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-foreground">Timeline</h3>
        <span className="text-xs text-muted-foreground">
          {segments.length} segment{segments.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Strip */}
      <div className="relative h-10 bg-background rounded overflow-hidden">
        {segments.map((seg) => {
          const startMs = Math.max(Date.parse(seg.start_ts) - fromMs, 0);
          const endMs = Math.min(Date.parse(seg.end_ts) - fromMs, windowMs);
          const leftPct = (startMs / windowMs) * 100;
          const widthPct = Math.max(((endMs - startMs) / windowMs) * 100, 0.3); // min visible width
          const isSelected = seg.segment_id === selectedSegmentId;
          const durationMs = endMs - startMs;

          const bgClass = isSelected
            ? TYPE_COLOURS_SELECTED[seg.type] ?? 'bg-accent'
            : TYPE_COLOURS[seg.type] ?? 'bg-secondary';
          const borderClass = SOURCE_BORDER[seg.source] ?? 'border-solid';

          return (
            <button
              key={seg.segment_id}
              title={`${seg.type} · ${SOURCE_LABEL[seg.source] ?? seg.source} · ${formatDuration(durationMs)} · ${seg.evidence_refs.length} events`}
              onClick={() =>
                onSelectSegment(isSelected ? null : seg)
              }
              className={`absolute top-0 h-full border-t-2 border-b-2 border-white/30 ${bgClass} ${borderClass} transition-all hover:brightness-125 cursor-pointer ${
                isSelected ? 'ring-2 ring-white/60 z-10' : ''
              }`}
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
              }}
            >
              {/* Show type label if segment is wide enough */}
              {widthPct > 8 && (
                <span className="text-xs text-white/80 font-medium truncate px-1 leading-10">
                  {seg.type}
                </span>
              )}
              {/* Order count badge (v0.2) */}
              {(seg.associated?.order_entity_ids?.length ?? 0) > 0 && (
                <span
                  className="absolute -top-2 -right-1 bg-ring text-white text-xs font-bold rounded-full min-w-4 h-3.5 flex items-center justify-center px-0.5 z-20 shadow"
                  title={`${seg.associated!.order_entity_ids!.length} order(s)`}
                >
                  {seg.associated!.order_entity_ids!.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Axis labels */}
      <div className="relative h-4">
        {axisLabels.map((a, i) => (
          <span
            key={i}
            className="absolute text-xs text-muted-foreground -translate-x-1/2"
            style={{ left: `${a.pct}%` }}
          >
            {a.label}
          </span>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 bg-success rounded-sm inline-block" /> Moving
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 bg-warning rounded-sm inline-block" /> Stopped
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-2 bg-secondary rounded-sm inline-block" /> Unknown
        </span>
        <span className="border-l border-border pl-3 flex items-center gap-1">
          <span className="w-4 border-t-2 border-white/50 border-solid inline-block" /> Observed
        </span>
        <span className="flex items-center gap-1">
          <span className="w-4 border-t-2 border-white/50 border-dashed inline-block" /> Declared
        </span>
        <span className="flex items-center gap-1">
          <span className="w-4 border-t-2 border-white/50 border-dotted inline-block" /> Inferred
        </span>
      </div>
    </div>
  );
}
